import React, { useEffect } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { Checkbox } from 'primereact/checkbox'
import { Tooltip } from 'primereact/tooltip'

import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import Text from '../../../ui/Text'
import { SLabelText } from '../../styles/Form'
import { ChargePointExternalServerWidget } from '../chargepoint-external-server-widget'
import { mapChargerToChargerInformation } from '../../utils/objectMappers'
import {
    ChargePoint,
    ExternalServer,
    useChargePointDisableOcppMutation,
    useChargePointEnableOcppMutation,
    useChargePointUpdateChargerInformationMutation,
    useExternalServerGetByChargePointIdQuery,
} from '../../redux/api/web-api'
import useText from '../../hooks/useText'
import { Input } from '../../uikit/Input'
import {
    MAX_WIFI_SSID_LENGTH,
    MAX_WIFI_PASSWORD_LENGTH,
    MAX_EXTERNAL_ID_LENGTH,
    MAX_CHARGEPOINT_DESCRIPTION_LENGTH,
    MAX_CHARGEPOINT_PARTNER_DESCRIPTION_LENGTH,
} from '../../constants/InputsLength'
import { useToast } from '../../uikit/ToastProvider'

interface ComponentProps {
    chargePoint: ChargePoint
    externalServersForPartner: ExternalServer[]
}

export const STooltipText = styled.span`
    margin-top: 3px;
    margin-left: 7px;
`

const ChargePointDetailsPartnerInputs = ({ chargePoint, externalServersForPartner }: ComponentProps) => {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()
    const {
        register,
        control,
        formState: { errors },
    } = useFormContext()

    const [chargePointEnableOcpp, { isLoading: enableLoading }] = useChargePointEnableOcppMutation()
    const [chargePointDisableOcpp, { isLoading: disableLoading }] = useChargePointDisableOcppMutation()
    const [updateChargePointInformation] = useChargePointUpdateChargerInformationMutation()

    const disableCpms = chargePoint?.ocppEnabled
    const toast = useToast()

    const { data: externalServerForChargePoint, refetch: fetchExternalServerForChargePoint } =
        useExternalServerGetByChargePointIdQuery({
            chargePointId: chargePoint.id,
        })

    const chargePointExternalServer = chargePoint?.additionalInfo?.externalServerId
    //get server options for both chargePoint/partner and remove duplicates

    const checkDuplicateExternalServerID =
        externalServersForPartner &&
        externalServersForPartner.some((item) => item?.id === externalServerForChargePoint?.id)

    const externalServersOptions =
        (externalServerForChargePoint &&
            !checkDuplicateExternalServerID &&
            externalServersForPartner?.concat(externalServerForChargePoint)) ||
        externalServersForPartner

    const disabledToggleCpms =
        chargePoint?.isCharging || (!chargePoint?.additionalInfo?.externalServerId && !chargePoint?.ocppEnabled)

    const toggleOcppAvailability = async (forceUpdate: boolean = false) => {
        try {
            if (chargePoint?.id) {
                if (chargePoint?.ocppEnabled) {
                    await chargePointDisableOcpp({
                        chargePointId: chargePoint?.id,
                        ...(forceUpdate && { forceUpdate }),
                    }).unwrap()
                    {
                        toast.notifySuccess(
                            chargePoint?.isOcpp
                                ? text('lblChargingEfficiencyServiceDisabled')
                                : text('lblOcppProxyDisabled')
                        )
                    }
                } else {
                    await chargePointEnableOcpp({
                        chargePointId: chargePoint?.id,
                        ...(forceUpdate && { forceUpdate }),
                    })
                        .unwrap()
                        .then(() => {
                            toast.notifySuccess(
                                chargePoint?.isOcpp
                                    ? text('lblChargingEfficiencyServiceEnabled')
                                    : text('lblOcppProxyEnabled')
                            )
                        })
                }
            }
        } catch (error) {
            toast.notifyError(text('lblChargingEfficiencyServiceDisableEnableError'))
        }
    }

    const updateExternalServer = async (option?: number | null) => {
        await updateChargePointInformation({
            chargerInformation: { ...mapChargerToChargerInformation(chargePoint), externalServerId: option },
            chargePointId: chargePoint.id,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgChargerUpdated'))
            })
            .catch(() => {
                toast.notifyError(text('msgChargePointNotUpdated'))
            })
    }

    const externalId = (connectorName: string, connectorId: number) => {
        const externalId = `externalId_${connectorId}`

        return (
            <div className="w-full" key={`connector-${connectorId}`}>
                <Input
                    type="text"
                    id={externalId}
                    label={connectorName}
                    data-testid={externalId}
                    {...register(externalId)}
                    error={errors[externalId]?.message}
                    maxLength={MAX_EXTERNAL_ID_LENGTH}
                />
            </div>
        )
    }
    const showChargerInformation = (chargePoint?.isOcpp && !chargePoint?.ocppEnabled) || !chargePoint?.isOcpp
    //fetch external server for chargePoint when external servr id is updated
    useEffect(() => {
        fetchExternalServerForChargePoint()
    }, [chargePointExternalServer, fetchExternalServerForChargePoint])

    return (
        <>
            {showChargerInformation && (
                <>
                    <div className="grid ">
                        <div className=" lg:col-6 col-12">
                            <Input
                                type="text"
                                label={text('lblDescriptionField')}
                                id="description"
                                data-testid="description-input"
                                {...register('description')}
                                error={errors.description?.message}
                                maxLength={MAX_CHARGEPOINT_DESCRIPTION_LENGTH}
                            />
                        </div>

                        <div className=" lg:col-6 col-12">
                            <Input
                                type="text"
                                id="partnerDescription"
                                data-testid="partnerDescription-input"
                                {...register('partnerDescription')}
                                label={text('lblPartnerDescriptionField')}
                                error={errors.partnerDescription?.message}
                                maxLength={MAX_CHARGEPOINT_PARTNER_DESCRIPTION_LENGTH}
                            />
                        </div>
                        <div className="col-12">
                            <Input
                                type="date"
                                label={text('lblInstallationDate')}
                                id="installationDate"
                                data-testid="installationDate-input"
                                {...register('installationDate')}
                            />
                        </div>
                    </div>
                </>
            )}
            {externalServersOptions && externalServersOptions?.length > 0 && (
                <>
                    <div data-testid="integrations-heading" className="mt-3 text-lg font-medium mb-2">
                        <Text code="btnIntegrations"></Text>
                    </div>
                    <ChargePointExternalServerWidget
                        enableLoading={enableLoading}
                        disableLoading={disableLoading}
                        disabledToggleCpms={disabledToggleCpms}
                        chargePoint={chargePoint}
                        disableCpms={disableCpms}
                        updateExternalServer={updateExternalServer}
                        toggleOcppAvailability={toggleOcppAvailability}
                        externalServersOptions={externalServersOptions}
                    />
                </>
            )}
            {showChargerInformation && (
                <>
                    <div data-testid="wifi-heading" className="mt-3 mb-3 text-lg font-medium mt-5">
                        <Text code="lblWifiSettings"></Text>
                    </div>

                    <div className="grid ">
                        <div className=" lg:col-6 col-12">
                            <Input
                                type="text"
                                id="wifiSsid"
                                autoComplete="off"
                                data-testid="wifiSsid-input"
                                {...register('wifiSsid')}
                                error={errors.wifiSsid?.message}
                                label={text('lblLocalWiFiSSIDField')}
                                maxLength={MAX_WIFI_SSID_LENGTH}
                            />
                        </div>

                        <div className=" lg:col-6 col-12">
                            <Input
                                type="password"
                                id="wifiPassword"
                                autoComplete="new-password"
                                data-testid="wifiPassword-input"
                                {...register('wifiPassword')}
                                error={errors.wifiPassword?.message}
                                label={text('lblLocalWiFiPasswordField')}
                                maxLength={MAX_WIFI_PASSWORD_LENGTH}
                            />
                        </div>
                    </div>

                    <div data-testid="report-heading" className="mt-3 text-lg font-medium">
                        <Text code="lblReportSettings"></Text>
                    </div>

                    <SLabelText>
                        <Text code="lblExternalIdForReportsField" />
                    </SLabelText>
                    <div className="grid ">
                        <div className="col-12">
                            <div className="flex gap-3">
                                {chargePoint.connectors?.map((connector, index) =>
                                    externalId(adaptConnectorNaming(connector?.name), connector?.connectorId ?? index)
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mt-5">
                        <label htmlFor="partnerAccessible" className="checkbox-form">
                            <Controller
                                name="partnerAccessible"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Checkbox
                                        id="partnerAccessible"
                                        data-testid="partnerAccessible-input"
                                        checked={value}
                                        onChange={(e) => {
                                            onChange(e?.checked)
                                        }}
                                    />
                                )}
                            />
                            <span data-testid="partnerAccessible" className="ml-2">
                                <Text code="lblAccessibleByRFIDField" />
                            </span>
                            <span className="ml-2">
                                <Tooltip content={text('lblAccessibleByRFIDFieldInfo')} className="large-content">
                                    <i
                                        className="custom-target-icon pi pi-info-circle pointer mr-2 mt-1"
                                        style={{ color: 'var(--text-color-info)' }}
                                    ></i>
                                </Tooltip>
                                <Tooltip target=".custom-target-icon" />
                            </span>
                        </label>
                    </div>

                    <div className="mt-2">
                        <Controller
                            name="hasServiceAgreement"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Checkbox
                                    id="hasServiceAgreement"
                                    data-testid="hasServiceAgreement-input"
                                    checked={value}
                                    onChange={(e) => {
                                        onChange(e?.checked)
                                    }}
                                />
                            )}
                        />
                        <span data-testid="has-service-agreement" className="ml-2">
                            <Text code="lblHasServiceAgreementField" />
                        </span>
                    </div>
                </>
            )}
        </>
    )
}

export default ChargePointDetailsPartnerInputs
